import { locales, type Locale } from './config'
import { routing } from './routing'

// next/navigation usePathname liefert beim Vorrendern den internen Pfad
// (/en/kostenlose-beratung), im Browser aber den uebersetzten
// (/en/free-consultation). Wer daraus sichtbaren Text baut, rendert auf Server
// und Client Verschiedenes (React #418). Beide Formen fuehren hier auf dasselbe
// Paar aus internem und uebersetztem Pfad zurueck, jeweils ohne Locale-Praefix.

type Route = { internal: string; localized: Record<Locale, string>; regexes: RegExp[] }

const dynamicSegment = /\[(\.\.\.)?[^\]]+\]/g

const toRegex = (template: string) =>
  new RegExp(`^${template.replace(dynamicSegment, (_, catchAll: string | undefined) => (catchAll ? '(.+)' : '([^/]+)'))}/?$`)

const fill = (template: string, values: string[]) => {
  let i = 0
  return template.replace(dynamicSegment, () => values[i++] ?? '')
}

// Statische Routen vor dynamischen, sonst gewinnt /leistungen/[slug] gegen
// /leistungen/technologie und /[slug] gegen jede einstufige Seite
const routes: Route[] = Object.entries(routing.pathnames)
  .map(([internal, value]) => {
    const localized = Object.fromEntries(locales.map((locale) => [locale, typeof value === 'string' ? value : value[locale]])) as Record<Locale, string>
    const templates = [...new Set([internal, ...Object.values(localized)])]
    return { internal, localized, regexes: templates.map(toRegex) }
  })
  .sort((a, b) => (a.internal.match(dynamicSegment)?.length ?? 0) - (b.internal.match(dynamicSegment)?.length ?? 0))

const stripLocale = (pathname: string) => {
  const [, first, ...rest] = pathname.split('/')
  return (locales as readonly string[]).includes(first) ? `/${rest.join('/')}` : pathname
}

export const resolvePathname = (pathname: string, locale: Locale): { internal: string; localized: string } => {
  const path = stripLocale(pathname).replace(/(.)\/$/, '$1') || '/'

  for (const route of routes) {
    for (const regex of route.regexes) {
      const match = regex.exec(path)
      if (!match) continue
      const values = match.slice(1)
      return { internal: fill(route.internal, values), localized: fill(route.localized[locale], values) }
    }
  }

  return { internal: path, localized: path }
}
